import { Route } from 'react-router-dom';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import { techDocsReportIssueAddonModule } from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import techDocsMermaidAddonModule from 'backstage-plugin-techdocs-addon-mermaid/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import { GitHubProfilePage } from './components/user/GitHubProfilePage';
import { apis } from './apis';
import { Root } from './components/Root';
import { githubAuthApiRef } from '@backstage/core-plugin-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
  convertLegacyPageExtension,
  convertLegacyPlugin,
} from '@backstage/core-compat-api';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import catalogUnprocessedPlugin from '@backstage/plugin-catalog-unprocessed-entities/alpha';
import devtoolsPlugin from '@backstage/plugin-devtools/alpha';
import adrPlugin from '@backstage-community/plugin-adr/alpha';
import announcementsPlugin from '@backstage-community/plugin-announcements/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import signalsPlugin from '@backstage/plugin-signals/alpha';

import {
  createFrontendModule,
  createRouteRef,
  PageBlueprint,
  type FrontendFeature,
} from '@backstage/frontend-plugin-api';
import homePlugin from '@backstage/plugin-home/alpha';
import { HomePage } from './components/home/HomePage';
import {
  entityValidationPlugin,
  EntityValidationPage,
} from '@backstage-community/plugin-entity-validation';
import { DevToolsPage } from './components/devtools/CustomDevToolsPage';
import specreqPlugin from '@infra360/plugin-specreq';
import { CustomCatalogPage } from './components/catalog/CustomCatalogPage';
import { entityPage } from './components/catalog/EntityPage';

import visualizerPlugin from '@backstage/plugin-app-visualizer';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import authPlugin from '@backstage/plugin-auth';

// Override the catalog plugin's default page extension using a frontend module
const customCatalogPageModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    PageBlueprint.make({
      // No 'name' property - creates extension ID 'page:catalog' to override default
      params: {
        path: '/catalog',
        noHeader: false,
        // Link this page to the catalog.catalogIndex route reference
        routeRef: createRouteRef({ aliasFor: 'catalog.catalogIndex' }),
        loader: async () => <CustomCatalogPage />,
      },
    }),
  ],
});

const legacyOptions = convertLegacyAppOptions({
  apis,
  plugins: [],
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        provider={{
          id: 'github-auth-provider',
          title: 'GitHub',
          message: 'Sign in using GitHub',
          apiRef: githubAuthApiRef,
        }}
      />
    ),
  },
  featureFlags: [
    {
      pluginId: '',
      name: 'specreq-plugin-enabled',
      description: 'Enables the specreq plugin',
    },
    {
      pluginId: '',
      name: 'announcements-plugin-enabled',
      description: 'Enables the announcements plugin',
    },
    {
      pluginId: '',
      name: 'validator-plugin-enabled',
      description: 'Enables the validator plugin',
    },
  ],
});

const customHomePage = PageBlueprint.make({
  // Note: No 'name' property - this creates extension ID 'page:home'
  // which overrides the default home page from homePlugin
  params: {
    path: '/',
    noHeader: true,
    loader: async () => <HomePage />,
  },
});
const customHomeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [customHomePage],
});

const entityValidationNfsPlugin = convertLegacyPlugin(entityValidationPlugin, {
  extensions: [
    convertLegacyPageExtension(EntityValidationPage, {
      name: 'entity-validation',
      path: '/entity-validation',
    }),
  ],
});

const customDevToolsModule = createFrontendModule({
  pluginId: 'devtools',
  extensions: [
    PageBlueprint.make({
      // Note: No 'name' property - this creates extension ID 'page:devtools'
      // which overrides the default devtools page from devtoolsPlugin
      params: {
        path: '/devtools',
        noHeader: true,
        loader: async () => <DevToolsPage />,
      },
    }),
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/github-profile" element={<GitHubProfilePage />} />
  </FlatRoutes>
);

const legacyRoot = (
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>
);

const app = createApp({
  features: [
    // convertLegacyAppRoot must come FIRST so that the secondary legacy plugins it creates
    // (api-docs, techdocs, org, etc.) are deduplicated in favour of the alpha plugins below.
    // deduplicateFeatures keeps the LAST occurrence, so earlier entries lose to later ones.
    ...convertLegacyAppRoot(legacyRoot, { entityPage }),
    legacyOptions,
    userSettingsPlugin,
    homePlugin,
    customHomeModule,
    searchPlugin,
    notificationsPlugin,
    signalsPlugin,
    techdocsPlugin,
    techDocsMermaidAddonModule,
    techDocsReportIssueAddonModule,
    scaffolderPlugin,
    orgPlugin,
    apiDocsPlugin,
    catalogUnprocessedPlugin,
    adrPlugin as unknown as FrontendFeature,
    announcementsPlugin,
    entityValidationNfsPlugin,
    devtoolsPlugin,
    customDevToolsModule,
    catalogImportPlugin,
    catalogPlugin,
    customCatalogPageModule, // Custom catalog page override module
    specreqPlugin,
    visualizerPlugin,
    catalogGraphPlugin,
    authPlugin,
  ],
});

export default app.createRoot();
